import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MessageService } from '@corelabs/angular-messages';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { finalizeLoader } from '@shared/components/loader/operators/handle-loader';
import { reflectionActions } from '../actions/reflection.actions';
import { ReflectionHttpService } from '../services/http.service';

@Injectable({
    providedIn: 'root',
})
export class ReflectionEffects {
    private readonly actions = inject(Actions);
    private readonly httpService = inject(ReflectionHttpService);
    private readonly messageService = inject(MessageService);
    private readonly translateService = inject(TranslateService);

    getReflection$ = createEffect(() => {
        return this.actions.pipe(
            ofType(reflectionActions.getReflection),
            switchMap(({ id, params }) =>
                this.httpService.getReflection(id, params).pipe(
                    finalizeLoader('library-get-reflection'),
                    map((payload) => reflectionActions.getReflectionSuccess({ id, params, payload })),
                    catchError((error: HttpErrorResponse) => {
                        if (error.status === 404) {
                            return of(reflectionActions.getReflectionNotAnswered({ id, params }));
                        }
                        return of(reflectionActions.getReflectionError({ id, params, code: error.status }));
                    }),
                ),
            ),
        );
    });

    submitReflection$ = createEffect(() => {
        return this.actions.pipe(
            ofType(reflectionActions.submitReflection),
            switchMap(({ id, value }) =>
                this.httpService.submitReflection(id, value).pipe(
                    map((payload) =>
                        reflectionActions.submitReflectionSuccess({
                            id,
                            params: { pathwayId: value.pathwayId, courseId: value.courseId, moduleId: value.moduleId },
                            payload,
                        }),
                    ),
                    catchError((error: HttpErrorResponse) => of(reflectionActions.submitReflectionError({ code: error.status }))),
                ),
            ),
        );
    });

    submitReflectionError$ = createEffect(
        () => {
            return this.actions.pipe(
                ofType(reflectionActions.submitReflectionError),
                tap(() => this.messageService.add(this.translateService.instant('toast.learning-portfolio.reflection-error'), 'danger')),
            );
        },
        { dispatch: false },
    );
}
